import { Modal as MaterialModal } from '@material-ui/core';
import { PropTypes } from 'prop-types';
import { propType as StyledSystemPropType } from '@styled-system/prop-types';
import React from 'react';
import styled from 'styled-components';
import { Image } from '../../image';
import { Box, Flex } from '../../grid';
import { breakpoints } from '../../../constants';
import { ModalButtons } from './modal-buttons';
import { ModalHeader } from './modal-header';
import { P } from '../../typography/new';

const Modal = ({
  backgroundImage,
  backgroundPosition,
  backgroundColor,
  borderRadius,
  className,
  buttons,
  contentButtons,
  children,
  handleBack,
  handleClose,
  onClose,
  modalTitle,
  hasHighlightedHeading,
  open,
  height,
  maxHeight,
  width,
  buttonTitle,
  buttonSecondaryTitle,
  buttonPrice,
  buttonContainerStyle,
  hasInnerScroll,
  childrenContainerStyle,
  wrapperStyle,
  headerBackgroundColor,
  headerButtonColor,
  footer,
  hideBottomDecal,
  variant = 'center', // 'center' or 'side'
  mx,
  headerButtonWithCircularBackground,
  headingHeader,
  disableEnforceFocus,
  header = (
    <ModalHeader
      modalTitle={modalTitle}
      handleClose={handleClose || onClose}
      headingHeader={headingHeader}
      handleBack={handleBack}
      backgroundColor={headerBackgroundColor}
      buttonColor={headerButtonColor}
      hasHighlightedHeading={hasHighlightedHeading}
      headerButtonWithCircularBackground={headerButtonWithCircularBackground}
    />
  ),
}) => {
  const closeHandler = handleClose || onClose;
  return (
    <StyledMaterialModal
      open={open}
      onClose={closeHandler}
      $variant={variant}
      disableEnforceFocus={disableEnforceFocus}
    >
      <ModalContent
        className={className}
        position="relative"
        overflowX={hasInnerScroll ? '' : 'hidden'}
        justifyContent="center"
        data-testid="modal"
        flexDirection="column"
        backgroundColor={backgroundColor}
        backgroundImage={backgroundImage}
        backgroundPosition={backgroundPosition}
        height={variant === 'side' ? '100vh' : height}
        width={variant === 'side' ? { _: '100%', md: '520px' } : width}
        maxHeight={variant === 'side' ? '100%' : maxHeight}
        mx={variant === 'side' ? '0' : '16px'}
        borderRadius={variant === 'side' ? '0' : borderRadius}
        $variant={variant}
        {...wrapperStyle}
      >
        <Box
          flexDirection="column"
          display="flex"
          pb={!(footer || buttons) && hideBottomDecal && '24px'}
          overflowX={hasInnerScroll ? '' : 'hidden'}
          overflowY={hasInnerScroll ? '' : 'auto'}
          height="inherit"
          {...childrenContainerStyle}
        >
          {header}

          <Flex flexDirection="column" positon="relative" justifyContent="space-between" flex="1 1 0">
            <Flex
              flexDirection="column"
              mx={mx || (variant === 'side' ? { xs: '16px', md: '32px' } : { xs: '24px', md: '32px' })}
              flex="1 1 0"
            >
              {children}
            </Flex>

            {contentButtons && (
              <Flex mt="32px" width="100%" flexDirection="row" justifyContent="space-between">
                {contentButtons}
              </Flex>
            )}

            {(footer || buttons) && (
              <Flex
                py="16px"
                mt={{ sm: '20px', lg: '24px' }}
                px={{ xs: '16px', md: '32px' }}
                flexDirection="column"
                position="sticky"
                bottom="0"
                width="100%"
                backgroundColor="transparent"
                zIndex="10"
                pb={variant === 'side' ? 'max(16px, env(safe-area-inset-bottom))' : '16px'}
                {...buttonContainerStyle}
              >
                {footer || (
                  <>
                    {buttonTitle && (
                      <Flex pt="2px" pb="12px" width="100%" flexDirection="row" justifyContent="space-between">
                        <Flex>
                          <P variant="medium">{buttonTitle}</P>
                        </Flex>
                        <Flex>
                          <Flex mr="12px">
                            <P variant="medium">{buttonPrice}</P>
                          </Flex>
                          <Flex color="grey500" textTransform="uppercase">
                            <P variant="medium">{buttonSecondaryTitle}</P>
                          </Flex>
                        </Flex>
                      </Flex>
                    )}
                    <Flex flexDirection="column" position="relative" width="100%" zIndex="100" justifyContent="center">
                      <ModalButtons buttons={buttons} />
                    </Flex>
                  </>
                )}
              </Flex>
            )}
            {!hideBottomDecal && (
              <Image
                marginTop={!(footer || buttons) && { sm: '20px', lg: '24px' }}
                userSelect="none"
                alt=""
                width="100%"
                zIndex="10"
                bottom="0"
                src="/static/images/decal/bottom-decal-modal.svg"
              />
            )}
          </Flex>
        </Box>
      </ModalContent>
    </StyledMaterialModal>
  );
};

const StyledMaterialModal = styled(MaterialModal)`
  align-items: ${props => (props.$variant === 'side' ? 'stretch' : 'center')};
  display: flex;
  justify-content: ${props => (props.$variant === 'side' ? 'flex-end' : 'center')};

  ${props =>
    props.$variant === 'side' &&
    `
    & .MuiBackdrop-root {
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      animation: fadeIn 0.3s ease-in-out;
    }
  `}

  @media (max-width: ${breakpoints.md}) {
    ${props =>
      props.$variant === 'side' &&
      `
      align-items: flex-end;
      justify-content: center;
    `}
  }
`;

const ModalContent = styled(Flex)`
  outline: none;
  background-image: ${props => (props.backgroundImage ? `url(${props.backgroundImage})` : undefined)};
  background-color: ${props => (props.backgroundColor ? props.backgroundColor : undefined)};
  background-position: ${props => (props.backgroundPosition ? props.backgroundPosition : 'center')};
  background-repeat: no-repeat;
  background-size: cover;

  @media (min-width: ${breakpoints.md}) {
    @keyframes slideInFromRight {
      from {
        transform: translateX(100%);
      }
      to {
        transform: translateX(0);
      }
    }

    ${props =>
      props.$variant === 'side' &&
      `
      animation: slideInFromRight 0.3s ease-in-out;
    `}
  }
`;

Modal.propTypes = {
  buttonPrice: PropTypes.string,
  buttons: PropTypes.shape({
    primary: PropTypes.node.isRequired,
    secondary: PropTypes.node,
  }),
  buttonSecondaryTitle: PropTypes.string,
  buttonTitle: PropTypes.string,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  contentButtons: PropTypes.node,
  handleBack: PropTypes.func,
  handleClose: PropTypes.func,
  onClose: PropTypes.func,
  hasFooterDivider: PropTypes.bool,
  height: StyledSystemPropType,
  maxHeight: StyledSystemPropType,
  header: PropTypes.node,
  modalTitle: PropTypes.string,
  open: PropTypes.bool.isRequired,
  showShadow: PropTypes.bool,
  headerBackgroundColor: PropTypes.string,
  headerButtonColor: PropTypes.string,
  footerBackgroundColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  footer: PropTypes.node,
  width: StyledSystemPropType,
  buttonContainerStyle: StyledSystemPropType,
  childrenContainerStyle: StyledSystemPropType,
  wrapperStyle: StyledSystemPropType,
  backgroundImage: PropTypes.string,
  backgroundPosition: PropTypes.string,
  borderRadius: StyledSystemPropType,
  hideBottomDecal: PropTypes.bool,
  hasHighlightedHeading: PropTypes.bool,
  variant: PropTypes.oneOf(['center', 'side']),
  mx: StyledSystemPropType,
  headerButtonWithCircularBackground: PropTypes.bool,
  hasInnerScroll: PropTypes.bool,
  headingHeader: PropTypes.bool,
  disableEnforceFocus: PropTypes.bool,
};

Modal.defaultProps = {
  buttonPrice: null,
  buttons: null,
  buttonSecondaryTitle: null,
  buttonTitle: null,
  className: undefined,
  contentButtons: null,
  handleClose: undefined,
  onClose: undefined,
  handleBack: null,
  hasFooterDivider: false,
  height: 'fit-content',
  maxHeight: { _: '100%', md: '85%' },
  width: '560px',
  borderRadius: '32px',
  modalTitle: null,
  showShadow: false,
  header: undefined,
  headerBackgroundColor: 'warmWhite100',
  headerButtonColor: 'grey500',
  backgroundColor: 'warmWhite2',
  footerBackgroundColor: 'warmWhite100',
  footer: undefined,
  buttonContainerStyle: null,
  childrenContainerStyle: null,
  wrapperStyle: null,
  backgroundImage: null,
  backgroundPosition: null,
  hideBottomDecal: true,
  hasHighlightedHeading: false,
  variant: 'center',
  mx: null,
  headerButtonWithCircularBackground: false,
  hasInnerScroll: false,
  headingHeader: false,
  disableEnforceFocus: false,
};

export { Modal };
